import MobileOrDesktopDialog from '@/components/common/mobileOrDesktopDialog';
import { PrimaryButton } from '@acko-tech/building-blocks.ui.button';
import { SelectorChips } from '@acko-tech/building-blocks.ui.selector-chips';
import LinkCTA from '@/components/common/linkCTA';
import Cancellation from '@/public/images/Cancellation.svg';
import ErrorIcon from '@/assets/Icons/error-caution.svg';

import { useState, useCallback } from 'react';
import { styled } from '@acko-tech/building-blocks.ui.common';
import { useDispatch, useSelector } from 'react-redux';
import {
    clearAppointments,
    selectAppointments,
    selectCardDetails,
} from '@/store/modules/appointmentSlice';
import { formatBookingDetails } from '@/components/fakeDoor/utils';
import { TAP_BTN_CANCEL_SLOT_CONFIRMATION } from '@/analytics/events';
import { trackEventAPI } from '@/analytics/middleware';
import { cancelAppointment } from '@/components/fakeDoor/api';
import toast from 'react-hot-toast';
import { ROUTEMAP } from '@/components/fakeDoor/constants';
import Router from '@/components/common/router';
import { getUserDetails } from '@/store/modules/userDataSlice';
import { getFirstName } from '@/utils';
import { useLoading } from '@/hooks/useLoading';

type Option = {
    id: string;
    value: string;
    isSelected: boolean;
};

const CustomChip = styled(SelectorChips)`
    min-width: auto;
`;

interface Props {
    show: boolean;
    onClose: () => void;
    ctaClick: () => void;
}

function NewJourneyModal({ show, onClose, ctaClick }: Props) {
    const appointmentDetails = useSelector(selectAppointments);
    const userDetails = useSelector(getUserDetails);
    const dispatch = useDispatch();

    const cardData = useSelector(selectCardDetails);
    console.log('debug', appointmentDetails, cardData);
    const { start, stop, isLoading } = useLoading('cancel-appointment');

    const cancelBooking = useCallback(() => {
        // Prevent multiple API calls
        if (isLoading) return;

        start();

        cancelAppointment(
            {
                appointment_id: appointmentDetails.appointment_id,
                garage_id: appointmentDetails?.garage_id || '',
                cancellation_reason: 'Change of plans',
            },
            appointmentDetails?.garage_id
        )
            .then(() => {
                stop();
                dispatch(clearAppointments());
                //toast.success('Previous booking was successfully cancelled');
                ctaClick();
            })
            .catch((error) => {
                stop();
                console.error('Cancellation error:', error);
                toast.error('Something went wrong. Please try again later.');
            });
    }, [isLoading, start, stop, appointmentDetails, ctaClick]);

    const handleResume = () => {
        window.location.href = cardData.navigatePath;
    };

    const name = getFirstName(userDetails?.userName || 'There');
    const isInProgress = appointmentDetails.status === 'PAYMENT_IN_PROGRESS';

    const modalText =
        cardData?.ctaName == 'Complete Payment'
            ? `It looks like you were in the middle of making a
                                payment. Starting a new selection will reset
                                your prior selections.`
            : `It looks like you were in the middle of booking your slot. Starting a new selection will reset
                                your prior selections.`;

    return (
        <>
            <MobileOrDesktopDialog show={show} onClose={onClose}>
                <div className="image-wrapper">
                    <ErrorIcon />
                </div>

                <>
                    {isInProgress ? (
                        <>
                            <div className="heading">
                                Hang Tight, We're on It!
                            </div>
                            <div className="sub-text">
                                It seems there's a slight delay from our payment
                                partner in updating your status. No need to
                                worry—your booking is being processed, and we’ll
                                send you a confirmation text as soon as it’s
                                done.
                            </div>
                            <div className="call-info">
                                If you need any assistance in the meantime, feel
                                free to reach out to us at 
                                <a href="tel:18002669293">1800-266-9293</a>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="heading">
                                {/* {`Hey ${name}`},
                        <br />
                        <br /> */}
                                {modalText}
                            </div>
                            <div className="sub-text">
                                Do you wish to proceed?
                            </div>
                        </>
                    )}
                </>
                <>
                    {!isInProgress && (
                        <div className="cta-wrapper">
                            {isLoading ? (
                                <PrimaryButton
                                    disabled={isLoading}
                                    //onClick={handleResume}
                                    loading={isLoading}
                                >
                                    Cancelling appointment...
                                </PrimaryButton>
                            ) : (
                                <>
                                    <PrimaryButton
                                        //disabled={!selectedValue}
                                        onClick={cancelBooking}
                                    >
                                        Yes Proceed
                                    </PrimaryButton>
                                    <LinkCTA
                                        text="No"
                                        clickHandler={onClose}
                                    ></LinkCTA>
                                </>
                            )}
                        </div>
                    )}
                </>
            </MobileOrDesktopDialog>
            <style jsx>{`
                .heading {
                    color: #121212;
                    font-size: 20px;
                    font-weight: 600;
                    line-height: 28px;
                }
                .sub-text {
                    color: #4b4b4b;
                    font-size: 16px;
                    font-weight: 400;
                    line-height: 24px;
                    padding: 16px 0;
                }
                .cta-wrapper {
                    margin: 12px 0;
                }
                .image-wrapper {
                    margin: 16px 0;
                }

                .call-info {
                    color: var(--Text-Secondary-grey400, #4b4b4b);
                    text-align: center;
                    margin-bottom: 20px;
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 400;
                    line-height: 20px;
                    border-radius: var(--B-radius-12px, 12px);
                    border: 1px solid rgba(255, 227, 128, 0.7);
                    background: var(--Yellow-Y50, #fff7e5);
                    padding: var(--spacing-12px, 12px);
                }
                .call-info a {
                    text-decoration: none;
                    color: inherit;
                }
                .location {
                    font-size: 16px;
                    font-weight: 500;
                    line-height: 24px;
                    display: flex;
                    gap: 8px;
                    color: #5920c5;
                    align-items: center;
                    margin-bottom: 12px;
                }
            `}</style>
        </>
    );
}

export default NewJourneyModal;
